export const isWeekday = (date: Date): boolean => {
  const day = date.getDay();

  return day !== 0 && day !== 6;
};

// Parst ein deutsches Datum "dd.MM.yyyy" (wie von toLocaleDateString('de-DE')
// erzeugt) zurück in ein Date. Wird genutzt, um lokalen DatePicker-State aus
// dem Store zu rehydrieren, wenn man zu einem Step zurückspringt.
export const parseGermanDate = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parts = value.split('.');

  if (parts.length !== 3) {
    return undefined;
  }

  const [ day, month, year ] = parts.map(part => parseInt(part, 10));

  if (!day || !month || !year) {
    return undefined;
  }

  const date = new Date(year, month - 1, day);

  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const findNextWeekday = (startDate: Date): Date => {
  const nextDate = new Date(startDate);

  nextDate.setDate(nextDate.getDate() + 1);

  while (!isWeekday(nextDate)) {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  return nextDate;
};

/**
 * Formatiert ein Datum als "dd.MM.yyyy" String
 */
const formatDateString = (date: Date): string => {
  return date.toLocaleDateString('de-DE');
};

/**
 * Prüft ob ein Datum ein Feiertag ist
 */
export const isHoliday = (date: Date, holidays: string[] = []): boolean => {
  const dateStr = formatDateString(date);

  return holidays.includes(dateStr);
};

/**
 * Prüft ob ein Datum ein Arbeitstag ist (kein Wochenende und kein Feiertag)
 */
export const isWorkday = (date: Date, holidays: string[] = []): boolean => {
  return isWeekday(date) && !isHoliday(date, holidays);
};

export interface ICutoffHours {
  default: number;
  friday: number;
}

/**
 * Ermittelt die Cutoff-Stunde für ein gegebenes Datum
 */
export const getCutoffHourForDate = (
  date: Date,
  cutoffHours: number | ICutoffHours
): number => {
  if (typeof cutoffHours === 'number') {
    return cutoffHours;
  }

  // Freitag = 5
  return date.getDay() === 5 ? cutoffHours.friday : cutoffHours.default;
};

/**
 * Prüft ob heute ein Arbeitstag ist und ob wir vor der Cutoff-Zeit sind
 */
export const canOrderForToday = (
  cutoffHours: number | ICutoffHours,
  holidays: string[] = []
): boolean => {
  const now = new Date();
  const currentHour = now.getHours();
  const cutoffHour = getCutoffHourForDate(now, cutoffHours);

  return isWorkday(now, holidays) && currentHour < cutoffHour;
};

/**
 * Gibt das heutige Datum zurück (ohne Uhrzeit), wenn es ein Arbeitstag ist
 */
export const getTodayIfWorkday = (holidays: string[] = []): Date | null => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return isWorkday(today, holidays) ? today : null;
};

/**
 * Findet den nächsten Arbeitstag (ab morgen)
 */
export const getNextWorkday = (holidays: string[] = []): Date => {
  const nextDay = new Date();

  nextDay.setHours(0, 0, 0, 0);
  nextDay.setDate(nextDay.getDate() + 1);

  while (!isWorkday(nextDay, holidays)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return nextDay;
};

/**
 * Findet den nächsten Arbeitstag nach einem gegebenen Datum
 */
export const getNextWorkdayAfter = (startDate: Date, holidays: string[] = []): Date => {
  const nextDay = new Date(startDate);

  nextDay.setDate(nextDay.getDate() + 1);

  while (!isWorkday(nextDay, holidays)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }

  return nextDay;
};

/**
 * Wochentage auf Deutsch
 */
const WEEKDAYS_DE = [ 'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag' ];

/**
 * Prüft ob ein Datum morgen ist
 */
const isTomorrow = (date: Date): boolean => {
  const tomorrow = new Date();

  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return date.getTime() === tomorrow.getTime();
};

/**
 * Generiert ein dynamisches Label für ein Datum
 * z.B. "Heute", "Morgen", oder "Montag"
 */
export const getDateLabel = (date: Date, isToday: boolean): string => {
  if (isToday) {
    return 'Heute';
  }

  if (isTomorrow(date)) {
    return 'Morgen';
  }

  return WEEKDAYS_DE[date.getDay()];
};

/**
 * Formatiert ein Datum kurz als "TT.MM."
 */
export const formatShortDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');

  return `${day}.${month}.`;
};

export interface IDateOption {
  date: Date;
  dateStr: string;
  label: string;
  isToday: boolean;
}

/**
 * Generiert eine Liste von Arbeitstag-Optionen
 */
export const getWorkdayOptions = (
  count: number,
  cutoffHours: number | ICutoffHours,
  holidays: string[] = []
): IDateOption[] => {
  const options: IDateOption[] = [];
  const canToday = canOrderForToday(cutoffHours, holidays);
  const today = getTodayIfWorkday(holidays);

  // Wenn heute möglich, starte mit heute
  if (canToday && today) {
    options.push({
      date: today,
      dateStr: today.toLocaleDateString('de-DE'),
      label: `${getDateLabel(today, true)} (${formatShortDate(today)})`,
      isToday: true
    });
  }

  // Fülle mit weiteren Arbeitstagen auf
  let currentDate = canToday && today ? today : new Date();

  currentDate.setHours(0, 0, 0, 0);

  while (options.length < count) {
    const nextDay = getNextWorkdayAfter(currentDate, holidays);

    options.push({
      date: nextDay,
      dateStr: nextDay.toLocaleDateString('de-DE'),
      label: `${getDateLabel(nextDay, false)} (${formatShortDate(nextDay)})`,
      isToday: false
    });
    currentDate = nextDay;
  }

  return options;
};

// Legacy export für Abwärtskompatibilität
export const getTodayIfWeekday = getTodayIfWorkday;
